<template>
  <div class="seo-page">
    <!-- Header with gradient, without double navigation -->
    <header class="header">
      <div class="header-content">
        <h1 class="main-title">Our SEO solutions</h1>
        <p class="subtitle">Boost your online identity with a customized SEO strategy</p>
      </div>
    </header>

    <div class="content-container">
      <!-- Introduction -->
      <section class="card intro-card">
        <div class="card-content">
          <h2 class="section-title">Enhance your digital presence with our SEO expertise</h2>
          <div class="text-and-illustration">
            <div class="text-content">
              <p>
                In a highly competitive digital environment, our agency positions itself as
                the ideal strategic partner to transform your online visibility. Specialized in
                designing and implementing high-performance SEO strategies, we offer
                tailored solutions that combine efficiency and sustainability, with the latest available techniques.
                With our natural referencing services, boost your online presence,
                optimize qualified traffic acquisition, and improve the overall performance of your professional website.
              </p><br>
              <p>
                ✓ <strong><i>UX / UI</i></strong> : Conceive a cutting-edge website in terms of technical architecture and user experience.
              </p><br>
              <p>
                ✓ <strong><i>Optimized content</i></strong> : Position your website on key requests and meet your visitors' expectations.
              </p><br>
              <p>
                ✓ <strong><i>Netlinking</i></strong> : Improve your online presence with backlink campaigns.
              </p>
            </div>
            <div class="illustration-container">
              <iframe 
                src="https://www.youtube.com/embed/b8UHJ8ORkMY" 
                title="SEO explanatory video" 
                frameborder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowfullscreen
                class="youtube-video">
              </iframe>
            </div>
          </div>
        </div>
      </section>

      <!-- What is SEO -->
      <section class="card what-is-card">
        <div class="card-content">
          <h2 class="section-title">What is SEO ?</h2>
          <div class="text-content">
            <p>
              Natural referencing (SEO) essentially consists of making a website visible on
              search engines. To achieve this, it is necessary to structure content in a
              coherent manner and to complete it by adding tags to images, keywords to titles, and
              links to external sources. We can also create backlinks, which helps to
              improve your website's ranking. Our approach combines technical and
              strategic analyses to ensure sustainable and effective referencing.
            </p>
          </div>
        </div>
      </section>

      <!-- Why choose our SEO services -->
      <section class="card why-choose-card">
        <div class="card-content">
          <h2 class="section-title">Why use our services for your SEO ?</h2>
          <div class="text-content">
            <p>
              With our SEO solutions, radically transform your online visibility and boost your business.
              To strengthen your digital presence and generate qualified traffic on your professional site,
              invest in our SEO strategies, designed with the most cutting-edge methodologies, adapted to your needs and your field of activity.              
            </p>
          </div>
        </div>

        <div align="center">
          <a href="https://www.web-passion.fr/prestations/" target="_blank"
            style="display: block; width: fit-content; margin: 0 auto; background-color: #7c3aed1a; color: inherit; padding: 10px; border-radius: 9999px;">
            <strong>Learn more about our SEO services</strong>
          </a><br>
        </div>

      </section>

      <!-- Our results -->
      <section class="results-section">
        <h2 class="results-title">Our results, what you can expect</h2>
        
        <div class="results-container">
          <!-- Graph and text content -->
          <div class="results-content">
            <!-- Graph column -->
            <div class="results-left">
              <img src="/img/traffic-trend-en.svg" alt="SEO traffic estimation trend" class="results-graph" />
            </div>
            
            <!-- Text column -->
            <div class="results-right">
              <p class="results-text">
                Our strategic approach to natural referencing produces measurable and sustainable results.
                As shown in the graph opposite, our clients generally see a
                significant increase in organic traffic from the moment SEO management begins, with regular growth
                throughout our collaboration.
              </p>
            </div>
          </div>
          
          <!-- Statistics -->
          <div class="stats-container">
            <div class="stat-item">
              <div class="stat-value">+200%</div>
              <div class="stat-label">Average traffic increase</div>
            </div>
            
            <div class="stat-item">
              <div class="stat-value">6 months</div>
              <div class="stat-label">Average time for significant results</div>
            </div>
            
            <div class="stat-item">
              <div class="stat-value">95%</div>
              <div class="stat-label">Client satisfaction rate</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Advantages (Cards) -->
      <div class="advantages-grid">
        <!-- Advantage 1 -->
        <div class="advantage-card">
          <div class="advantage-number">1</div>
          <h3 class="advantage-title">Develop your web presence</h3>
          <p class="advantage-description">
            Our SEO strategies allow you to significantly develop your company's online reputation.
            Thanks to optimal positioning on strategic keywords, your brand gains
            visibility and credibility with your target audience.
          </p>
        </div>

        <!-- Advantage 2 -->
        <div class="advantage-card">
          <div class="advantage-number">2</div>
          <h3 class="advantage-title">Increase your qualified traffic</h3>
          <p class="advantage-description">
            Our SEO approach specifically targets users interested in your products or services,
            thus generating highly qualified traffic. Visitors attracted by natural referencing
            are more likely to become prospects.
          </p>
        </div>

        <!-- Advantage 3 -->
        <div class="advantage-card">
          <div class="advantage-number">3</div>
          <h3 class="advantage-title">Grow your conversion rate</h3>
          <p class="advantage-description">
            Thanks to our expertise in user experience optimization combined with SEO, we
            increase not only your traffic but also your conversion rate. Our global strategy
            aims to transform visitors into customers.
          </p>
        </div>

        <!-- Advantage 4 -->
        <div class="advantage-card">
          <div class="advantage-number">4</div>
          <h3 class="advantage-title">Reduce your advertising costs</h3>
          <p class="advantage-description">
            Natural referencing offers excellent long-term ROI. Unlike paid
            advertising campaigns, SEO generates continuous traffic without cost per click, reducing
            significantly your advertising investments.
          </p>
        </div>

        <!-- Advantage 5 -->
        <div class="advantage-card">
          <div class="advantage-number">5</div>
          <h3 class="advantage-title">Ensure a well-positioned website for the long term</h3>
          <p class="advantage-description">
            Our SEO strategies are designed for sustainable results. We establish
            solid foundations that continue to bear fruit over the long term, ensuring
            a lasting online presence and regular growth.
          </p>
        </div>

        <!-- Advantage 6 -->
        <div class="advantage-card">
          <div class="advantage-number">6</div>
          <h3 class="advantage-title">Improve your customer experience</h3>
          <p class="advantage-description">
            SEO is not limited to referencing. Our optimizations also improve the user
            experience of your site, with optimized loading times, intuitive navigation,
            and relevant content that meets your visitors' expectations.
          </p>
        </div>
      </div>

      <!-- Our SEO services -->
      <h2 class="services-title">What we offer for SEO</h2>
      
      <!-- Services grid -->
      <div class="services-grid">
        <!-- Service 1 -->
        <div class="service-card">
          <h3 class="service-title">Comprehensive audit</h3>
          <p class="service-description">
            Our goal is to conduct a thorough analysis of your website and then focus
            on actions that have the most impact on your referencing. Our audits cover technical accessibility,
            content quality, and the popularity of your site.
          </p>
          <ul class="service-features">
            <li>Technical audit for robots and users</li>
            <li>Content audit for engagement and relevance</li>
            <li>Popularity audit for thematic legitimacy</li>
          </ul>
        </div>
        
        <!-- Service 2 -->
        <div class="service-card">
          <h3 class="service-title">OnSite support</h3>
          <p class="service-description">
            We seek to discover new opportunities for your website by adding
            complementary corrective actions. Our OnSite approach optimizes all internal aspects
            of your site to maximize its referencing potential.
          </p>
          <ul class="service-features">
            <li>Content optimization</li>
            <li>Regular deliverables</li>
            <li>Regular exchanges</li>
          </ul>
        </div>
        
        <!-- Service 3 -->
        <div class="service-card">
          <h3 class="service-title">OffSite support</h3>
          <p class="service-description">
            The main objective is to evaluate existing inbound links and develop
            new links to the strategic pages of your site. Our OffSite strategy strengthens
            the authority and credibility of your domain.
          </p>
          <ul class="service-features">
            <li>Netlinking strategy</li>
            <li>Generation of qualified backlinks</li>
            <li>Popularity optimization</li>
          </ul>
        </div>
      </div>

      <!-- Client testimonials -->
      <section class="testimonials-section">
        <h2 class="testimonials-title">What our clients say</h2>
        
        <div class="testimonials-container">
          <!-- Testimonial 1 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Charles R.</h3>
                <p class="client-company">Real Estate Agent</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>Following my collaboration with Arynae agency for SEO content writing,
                I am fully satisfied with the results obtained. The team perfectly understood the challenges of my business
                and the very comprehensive briefs respected my editorial line.
                The delivery deadline was met and communication was smooth throughout the project.
                The percentage of positioned web pages went from 10% to 45% in a few months. I highly recommend their SEO writing
                services.
              </p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Testimonial 2 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Myriam H.</h3>
                <p class="client-company">E-commerce store owner</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>Arynae's team helped me with the complete redesign and migration of my online store.
                Technical problems, content, design - we went through everything.
                After a few weeks of restructuring, the migration went without a hitch.
                Six weeks after going online, my organic traffic increased by 45% and my conversions by 30%.
              </p>
            </div>
            <div class="testimonial-border"></div>
          </div>
          
          <!-- Testimonial 3 -->
          <div class="testimonial-card">
            <div class="testimonial-header">
              <div class="client-info">
                <h3 class="client-name">Émilie D.</h3>
                <p class="client-company">Communications Officer</p>
                <div class="rating">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
            </div>
            <div class="testimonial-body">
              <p>I called on Arynae to develop my netlinking strategy and I don't regret this choice.
                Their qualitative rather than quantitative approach convinced me from the start.
                The agency identified relevant sites in my field of activity and managed to obtain
                quality backlinks: over a two-year period, my site's authority went from 15 to 47.
                I particularly appreciate the detailed reports on the links obtained and their impact.
                The results are there with constant progression in the SERPs
                since the beginning of the campaign. A professional collaboration that I intend to continue.
              </p>
            </div>
            <div class="testimonial-border"></div>
          </div>
        </div>
        
        <div class="dots-navigation">
          <span class="dot active"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </section>

      <!-- Call to action -->
      <div class="cta-container">
        <span class="cta-button fake-link" @click="navigateTo('e1')">Schedule an online / phone / video appointment</span>
      </div>
    </div>
  </div>
</template>


<script>
import { useHead } from '@vueuse/head'

export default {
  name: 'SeoServicePage',
  setup() {
    useHead({
      title: 'SEO services - Organic search optimization experts | Arynae',
      meta: [
        { name: 'description', content: 'Discover our SEO organic search services. Complete audit, on-site and off-site optimization to improve your visibility and increase your qualified traffic.' },
        { name: 'keywords', content: 'SEO services, organic search, SEO audit, on-site optimization, netlinking, backlinks, SEO strategy, technical audit, Arynae' },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Essential Open Graph
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'SEO services - Organic search optimization experts | Arynae' },
        { property: 'og:description', content: 'Discover our SEO organic search services. Complete audit, on-site and off-site optimization to improve your visibility and increase your qualified traffic.' },
        { property: 'og:url', content: 'https://arynae.com/en/seo' },
        { property: 'og:locale', content: 'en_US' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Digital agency - No Code solutions, SaaS, Chatbots & SEO' },
        // Basic Twitter Card
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'SEO services - Organic search optimization experts | Arynae' },
        { name: 'twitter:description', content: 'Discover our SEO organic search services. Complete audit, on-site and off-site optimization to improve your visibility and increase your qualified traffic.' },
        { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://arynae.com/en/seo' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com/seo' },
        { rel: 'alternate', hreflang: 'en', href: 'https://arynae.com/en/seo' }
      ],
      htmlAttrs: {
        lang: 'en'
      }
    })
    
    return {}
  },
  data() {
    return {}
  },
  methods: {
    navigateTo(code) {
      const routes = {
        'e1': 'https://cal.com/web-passion/rdv'
      };
      
      window.open(routes[code] || code, '_blank');
    }
  }
}
</script>


<style scoped>
.seo-page {
  font-family: Arial, sans-serif;
  color: #333;
}

/* Header Styles */
.header {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  padding-bottom: 6rem;
  color: white;
}

.header-content {
  text-align: center;
  padding: 5rem 2rem 3rem; /* Augmenté le padding-top pour compenser l'absence de la navigation */
}

.main-title {
  font-size: 2.75rem;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto;
}

/* Content Container */
.content-container {
  max-width: 1200px;
  margin: -3rem auto 4rem;
  padding: 0 2rem;
}

/* Card Styles */
.card {
  background: linear-gradient(to bottom, #ffffff, #f3f4f6);
  border-radius: 0.5rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  margin-bottom: 2rem;
  overflow: hidden;
}

.card-content {
  padding: 2rem;
}

.section-title {
  font-size: 1.75rem;
  margin-bottom: 1.5rem;
  color: #333;
  display: block; /* Assure que le titre occupe toute la largeur */
}

.text-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
}

/* Intro Card with Illustration */
.text-and-illustration {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
}

.text-and-illustration .text-content {
  flex: 1;
  min-width: 300px;
}

/* Conteneur d'illustration */
.illustration-container {
  flex: 0.7;  /* Changé de flex: 1 à flex: 2 pour donner plus d'espace à la vidéo */
  min-width: 200px;  /* Augmenté pour garantir plus d'espace horizontal */
  height: 300px;  /* Légèrement augmenté en hauteur */
  display: flex;
  justify-content: center;
  align-items: center;
}

.youtube-video {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.illustration-img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 10px;
}

/* Section Résultats */
/* Section Résultats */
.results-section {
  background-color: #f9fafb;
  padding: 3rem 2rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
}

.results-title {
  font-size: 1.75rem;
  color: #333;
  margin-bottom: 2rem;
}

.results-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.results-content {
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
}

.results-left {
  flex: 2;
  min-width: 450px;
}

.results-img {
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.results-right {
  flex: 1;
  min-width: 300px;
}

.results-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 0;
}

.stats-container {
  display: flex;
  flex-wrap: wrap;
  gap: 2rem;
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-top: 1rem;
}

.stat-item {
  flex: 1;
  min-width: 120px;
  text-align: center;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: bold;
  color: #2563EB;
  margin-bottom: 0.75rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #555;
  line-height: 1.4;
}

/* Titre des services */
.services-title {
  font-size: 2rem;
  margin: 4rem 0 2.5rem;
  color: #333;
  text-align: center;
  position: relative;
}

.services-title:after {
  content: "";
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #2563EB, #7C3AED);
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
}

.service-card {
  background: white;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.service-card:before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  background: linear-gradient(to right, #2563EB, #7C3AED);
}

.service-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
}

.service-title {
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #333;
  font-weight: bold;
}

.service-description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 1.5rem;
}

.service-features {
  margin-top: 1rem;
  padding-left: 0;
  list-style: none;
}

.service-features li {
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  color: #555;
  position: relative;
  padding-left: 1.5rem;
}

.service-features li:before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #2563EB;
  font-weight: bold;
}

/* Advantages Grid */
.advantages-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
}

.advantage-card {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  position: relative;
}

.advantage-number {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  margin-bottom: 1rem;
}

.advantage-title {
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: #333;
}

.advantage-description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: #555;
}

/* Testimonials Section - Style moderne */
.testimonials-section {
  padding: 5rem 0;
  background: #ffffff;
  margin: 5rem 0;
  position: relative;
  overflow: hidden;
}

.testimonials-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 10% 20%, rgba(37, 99, 235, 0.05) 0%, rgba(124, 58, 237, 0.05) 90%);
  z-index: 0;
}

.testimonials-title {
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;
  position: relative;
  z-index: 1;
}

.testimonials-title::after {
  content: "";
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  border-radius: 2px;
}

.testimonials-container {
  display: flex;
  gap: 2rem;
  padding: 1rem 4rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
}

.testimonials-container::-webkit-scrollbar {
  display: none;
}

.testimonial-card {
  background: white;
  border-radius: 16px;
  min-width: 320px;
  flex: 1;
  position: relative;
  scroll-snap-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
}

.testimonial-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 0 12px rgba(0, 0, 0, 0.06);
}

.testimonial-header {
  display: flex;
  align-items: center;
  padding: 1.5rem 1.5rem 0.5rem;
}

.client-info {
  flex: 1;
}

.client-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
  color: #1a202c;
}

.client-company {
  font-size: 0.9rem;
  color: #718096;
  margin: 0 0 0.5rem;
}

.rating {
  display: flex;
  font-size: 1.1rem;
  color: #FFC107;
}

.testimonial-body {
  padding: 0.5rem 1.5rem 2rem;
  font-size: 1rem;
  line-height: 1.7;
  color: #4A5568;
  flex: 1;
}

.testimonial-border {
  height: 5px;
  background: linear-gradient(90deg, #2563EB, #7C3AED, #EC4899);
  width: 100%;
}

.dots-navigation {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2.5rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #CBD5E0;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot:hover {
  background-color: #A0AEC0;
}

.dot.active {
  background: linear-gradient(90deg, #2563EB, #7C3AED);
  width: 30px;
  border-radius: 10px;
}

/* Testimonials Section - Correction responsive */
.testimonials-container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  padding: 1rem;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  overflow-x: auto;
  padding-bottom: 1rem; /* Espace pour la scrollbar */
}

.testimonial-card {
  flex: 0 0 calc(33.333% - 1.5rem);
  min-width: 280px;
  background: white;
  border-radius: 16px;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04), 0 0 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Cache la scrollbar sur certains navigateurs mais garde la fonctionnalité */
.testimonials-container::-webkit-scrollbar {
  height: 6px;
}

.testimonials-container::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.testimonials-container::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.05);
}

@media (max-width: 768px) {
  .testimonials-container {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

.dots-navigation {
  display: none;
}

.dots-navigation {
  display: none; /* Cache les points s'ils ne sont pas nécessaires */
}

@media (max-width: 992px) {
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .service-card {
    padding: 2rem;
    margin-bottom: 1.5rem;
  }

  
  .main-title {
    font-size: 2rem;
  }
  
  .content-container {
    padding: 0 1rem;
  }
  
  .advantages-grid {
    grid-template-columns: 1fr;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .text-and-illustration {
    flex-direction: column;
  }
  
  /* Intro Card - Fix pour la section introduction SEO */
  .intro-card .text-and-illustration {
    flex-direction: column;
  }
  
  .intro-card .text-content {
    width: 100%;
    min-width: 100%;
    order: 1;
    margin-bottom: 1rem;
  }
  
  .intro-card .illustration-container {
    width: 100%;
    min-width: 100%;
    order: 2;
    height: 200px;
  }
  
  .intro-card .section-title {
    font-size: 1.5rem;
    text-align: center;
  }
  
  .illustration-container {
    height: 200px; /* Hauteur réduite sur mobile */
  }
  
  .results-section {
    padding: 2rem 1rem;
  }
  
  .results-content {
    flex-direction: column;
    gap: 2rem;
  }

  .results-left, .results-right {
    min-width: 100%;
  }
  
  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
  
  .stat-item {
    min-width: 100%;
  }
}


.client-name {
  font-weight: bold;
  font-style: normal;
  margin-top: 1rem;
}

/* Call to Action */
.cta-container {
  text-align: center;
  margin: 3rem 0;
}

.cta-button {
  background: linear-gradient(to right, #2563EB, #7C3AED);
  color: white;
  font-size: 1.25rem;
  font-weight: bold;
  padding: 1rem 2.5rem;
  border: none;
  border-radius: 2rem;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: inline-block;
  max-width: 100%;
  word-wrap: break-word;
}

@media (max-width: 768px) {
  .cta-button {
    font-size: 1rem;
    padding: 0.8rem 1.5rem;
    width: 90%;
  }
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.25);
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .service-card {
    padding: 2rem;
    margin-bottom: 1.5rem;
  }

  
  .main-title {
    font-size: 2rem;
  }
  
  .content-container {
    padding: 0 1rem;
  }
  
  .advantages-grid {
    grid-template-columns: 1fr;
  }
  
  .services-grid {
    grid-template-columns: 1fr;
  }
  
  .text-and-illustration {
    flex-direction: column;
  }
  
  .illustration-container {
    height: 200px; /* Hauteur réduite sur mobile */
  }
  
  .results-section {
    padding: 2rem 1rem;
  }
  
  .results-content {
  flex-direction: column;
  gap: 2rem;
}

.results-left, .results-right {
  min-width: 100%;
}
  
  .stats-container {
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }
  
  .stat-item {
    min-width: 100%;
  }
}
</style>